import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";
import { cityPriceFaq } from "./cityPricing";

const ramatuelle: ChauffeurPage = {
  slug: "ramatuelle",
  title: "Chauffeur privé à Ramatuelle – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Ramatuelle : transferts aéroport de Nice (~1 h 30), navette gare TGV de Saint-Raphaël, plages de Pampelonne et village perché, dans la presqu’île de Saint-Tropez, 7j/7.",
  keywords: [
    "chauffeur privé ramatuelle",
    "vtc ramatuelle",
    "vtc pampelonne",
    "transfert ramatuelle aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Ramatuelle",
    subtitle:
      "Des plages de Pampelonne au village perché, dans la presqu’île de Saint-Tropez : transferts aéroport de Nice (~1 h 30) et dépose aux beach clubs, 24h/24.",
    keywords: [
      "VTC Ramatuelle",
      "chauffeur Pampelonne",
      "beach club Pampelonne",
      "transfert aéroport Nice",
    ],
    imageSrc: heroImage.src,
    imageAlt:
      "Chauffeur privé & VTC à Ramatuelle, plages de Pampelonne et village perché",
    badges: [
      "Aéroport de Nice ~1 h 30",
      "Dépose plages de Pampelonne",
      "Chauffeurs bilingues",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Ramatuelle",
    items: [
      {
        title: "Dépose aux beach clubs de Pampelonne",
        description:
          "Route étroite et parkings saturés en été : le chauffeur vous dépose à l’entrée des clubs de la plage de Pampelonne et revient sur appel.",
      },
      {
        title: "Aéroport de Nice en ~1 h 30",
        description:
          "La presqu’île de Saint-Tropez est la plus éloignée : nous prévoyons une large marge et suivons votre vol pour ce transfert long.",
      },
      {
        title: "Village perché sans stationnement",
        description:
          "Les ruelles circulaires du village médiéval sont piétonnes : dépose au pied des remparts, reprise au même endroit.",
      },
      {
        title: "Vignobles et presqu’île",
        description:
          "Domaines des Côtes de Provence, Cap Camarat, l’Escalet : itinéraires sur mesure, note 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Ramatuelle",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Ramatuelle ⇄ aéroport de Nice Côte d’Azur en ~1 h 30, avec suivi de vol et large marge anticipée.",
      },
      {
        title: "Navette plages de Pampelonne",
        text: "Dépose et reprise à l’entrée des beach clubs, sans souci de parking ni de route saturée.",
      },
      {
        title: "Village perché & Saint-Tropez",
        text: "Liaison entre le village médiéval, Saint-Tropez (à quelques minutes) et les criques de la presqu’île.",
      },
      {
        title: "Route des vignobles",
        text: "Domaines des Côtes de Provence autour de Ramatuelle, dégustations et dîners, chauffeur dédié au retour.",
      },
      {
        title: "Navette gare TGV de Saint-Raphaël & longue distance",
        text: "Liaison vers la gare de Saint-Raphaël-Valescure et trajets depuis Cannes, Nice ou l’aéroport.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Plage de Pampelonne",
        description:
          "Beach clubs et parkings, dépose au plus près de l’entrée.",
      },
      {
        name: "Village perché de Ramatuelle",
        description: "Remparts, ruelles circulaires et Théâtre de Verdure.",
      },
      {
        name: "Cap Camarat & phare",
        description:
          "Pointe de la presqu’île et l’un des plus hauts phares de France.",
      },
      {
        name: "L’Escalet",
        description: "Crique et départ du sentier du littoral vers Pampelonne.",
      },
      {
        name: "Domaines viticoles",
        description: "Vignobles des Côtes de Provence autour du village.",
      },
      {
        name: "Aéroport de Nice",
        description: "Terminaux 1 et 2, à ~1 h 30 par l’A8.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur de Pampelonne au village perché",
    subheading:
      "Ramatuelle, c’est la plage de Pampelonne en bas et un village médiéval en balcon sur la presqu’île.",
    paragraphs: [
      "Dans la presqu’île de Saint-Tropez, Ramatuelle réunit la célèbre plage de Pampelonne et ses beach clubs, un village médiéval perché aux ruelles circulaires et les vignobles des Côtes de Provence. KS Rent Services y assure vos déplacements avec chauffeur privé : dépose aux clubs, liaison avec Saint-Tropez et mises à disposition, sans jamais affronter les parkings saturés de l’été.",
      "L’aéroport de Nice Côte d’Azur est à environ 1 h 30 : la presqu’île est le point le plus éloigné de nos villes, aussi nous prévoyons une large marge et suivons votre vol. Pour le train, la gare TGV de Saint-Raphaël sert de point de transfert à l’entrée du golfe.",
      "Pendant le Festival de Ramatuelle en août, comme lors des Voiles de Saint-Tropez, la circulation se densifie sur la presqu’île : le chauffeur adapte les horaires, privilégie la dépose au plus près et revient sur simple appel, pour Pampelonne comme pour le village.",
    ],
    bullets: [
      "Aéroport de Nice en ~1 h 30",
      "Dépose aux beach clubs de Pampelonne",
      "Village perché & vignobles",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Ramatuelle",
    steps: [
      "Indiquez votre trajet (aéroport, Pampelonne, village, Saint-Tropez) et vos horaires.",
      "Nous confirmons le véhicule, la large marge pour l’aéroport de Nice et le forfait.",
      "Le chauffeur suit votre vol et vous accueille à l’aéroport ou à l’adresse convenue.",
      "Dépose aux beach clubs ou au pied du village, reprise sur simple appel.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Ramatuelle"),
    {
      q: "Combien de temps pour l’aéroport de Nice ?",
      a: "Comptez environ 1 h 30. La presqu’île de Saint-Tropez est la plus éloignée de l’aéroport de Nice : nous prévoyons une large marge et suivons votre vol.",
    },
    {
      q: "Déposez-vous aux beach clubs de la plage de Pampelonne ?",
      a: "Oui, c’est l’usage le plus demandé : nous vous déposons à l’entrée du club et revenons vous chercher sur appel, sans souci de parking ni de route saturée.",
    },
    {
      q: "Intervenez-vous pendant le Festival de Ramatuelle ?",
      a: "Oui, en août le village est très fréquenté : nous adaptons les horaires et la dépose autour du Théâtre de Verdure, comme pour Les Voiles de Saint-Tropez.",
    },
    {
      q: "Quelle est la gare TGV la plus proche ?",
      a: "La gare de Saint-Raphaël-Valescure, à l’entrée du golfe : nous assurons la navette pour vos départs et arrivées en TGV.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en été et pendant les événements de la presqu’île ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et les bagages.",
    },
  ],
  relatedLinks: [
    {
      label: "Réserver un chauffeur à Saint-Tropez",
      href: "/chauffeur/saint-tropez",
    },
    { label: "Service de chauffeur à Cannes", href: "/chauffeur/cannes" },
    {
      label: "Chauffeur privé sur la Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Location avec livraison à Saint-Tropez",
      href: "/location/location-voiture-saint-tropez",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Ramatuelle",
    text: "Transfert aéroport de Nice, dépose à Pampelonne ou mise à disposition dans la presqu’île : réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Ramatuelle 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé à Ramatuelle : transferts aéroport de Nice (~1 h 30), dépose aux plages de Pampelonne, village perché et vignobles de la presqu'île.",
    canonical: "/chauffeur/ramatuelle",
  },
};

export default ramatuelle;
